/**
 * Product page behaviour for sections/dorahi-product.liquid.
 * Vanilla port of the client components used by
 * dorahi-main/app/products/[handle]/page.tsx:
 *
 *  - product-context.tsx + variant-selector.tsx  -> option state, availability,
 *    URL ?variant= sync, price / mega-deal / urgency repaint
 *  - countdown-timer.tsx                         -> stable deal window countdown
 *  - stock-urgency.tsx                           -> seeded "Only N left" line
 *  - product-gallery.tsx                         -> mobile dots, lightbox, PiP video
 *  - mega-deal.tsx / size-chart-modal.tsx        -> bottom sheets
 *  - add-to-cart.tsx                             -> /cart/add + CartLinesUpdateEvent
 *    (same event flow as dorahi-product-card.js so Horizon's cart drawer
 *    re-renders and auto-opens)
 *  - buy-now.tsx                                 -> Shiprocket buyDirect, with a
 *    checkout-permalink fallback so the button never fails silently
 */
import { CartLinesUpdateEvent } from "@shopify/events";

class DorahiProduct extends HTMLElement {
  connectedCallback() {
    const dataEl = this.querySelector("[data-product-json]");
    this.product = dataEl
      ? JSON.parse(dataEl.textContent)
      : { options: [], variants: [] };
    this.currencySymbol = this.dataset.currencySymbol || "₹";
    this.couponValue = Number(this.dataset.couponValue || 15);

    // state: { optionNameLowerCase: value } — mirrors ProductProvider.
    this.state = {};
    const initial =
      this.currentVariantFromUrl() || this.firstAvailableVariant();
    if (initial) {
      this.product.options.forEach((name, index) => {
        this.state[name.toLowerCase()] = initial.options[index];
      });
    }

    this.paintSocialProof();
    this.bindOptions();
    this.bindGallery();
    this.bindModals();
    this.bindCountdown();
    this.bindCart();
    this.render();
  }

  /* ---------------- variant state ---------------- */

  currentVariantFromUrl() {
    const param = new URLSearchParams(window.location.search).get("variant");
    if (!param) return null;
    return this.product.variants.find((v) => String(v.id) === param) || null;
  }

  firstAvailableVariant() {
    return (
      this.product.variants.find((v) => v.available) ||
      this.product.variants[0] ||
      null
    );
  }

  selectedVariant() {
    return (
      this.product.variants.find((variant) =>
        this.product.options.every(
          (name, index) =>
            this.state[name.toLowerCase()] === variant.options[index],
        ),
      ) || null
    );
  }

  bindOptions() {
    this.querySelectorAll("[data-option-button]").forEach((button) => {
      button.addEventListener("click", () => {
        if (button.disabled) return;
        this.state[button.dataset.optionName] = button.dataset.optionValue;
        this.render();
        const variant = this.selectedVariant();
        if (variant) {
          const url = new URL(window.location.href);
          url.searchParams.set("variant", variant.id);
          window.history.replaceState(null, "", url.toString());
        }
      });
    });
  }

  // Mirrors variant-selector.tsx: a value is buyable if some available variant
  // matches it plus every *other* currently selected option.
  valueIsAvailable(optionName, value) {
    const candidate = { ...this.state, [optionName]: value };
    return this.product.variants.some(
      (variant) =>
        variant.available &&
        this.product.options.every(
          (name, index) =>
            candidate[name.toLowerCase()] === variant.options[index],
        ),
    );
  }

  render() {
    const variant = this.selectedVariant();

    // Option buttons
    this.querySelectorAll("[data-option-button]").forEach((button) => {
      const face = button.querySelector("[data-option-face]");
      const active =
        this.state[button.dataset.optionName] === button.dataset.optionValue;
      const available = this.valueIsAvailable(
        button.dataset.optionName,
        button.dataset.optionValue,
      );
      button.disabled = !available;
      if (face) {
        face.classList.toggle("text-dorahi-pink", active && available);
        // The server-rendered selected button uses `!border-[#fd184d]`
        // (Tailwind's !important modifier — dorahi-squircle.js's border
        // overlay needs it to beat the squircle's own border-color). Toggle
        // the same class here, not the non-important variant: otherwise
        // switching options leaves the originally SSR-selected button stuck
        // with a pink border forever, since !important always wins over the
        // plain "border-[#d1d5dc]" class this would otherwise add.
        face.classList.toggle("!border-[#fd184d]", active && available);
        face.classList.toggle("text-text-main", !active && available);
        // Sold-out values always keep the default gray border, even when
        // they match the current selection.
        face.classList.toggle("border-[#d1d5dc]", !active || !available);
        face.classList.toggle("opacity-50", !available);
        face.classList.toggle("cursor-not-allowed", !available);
        face.classList.toggle("text-gray-400", !available);
        face.classList.toggle("line-through", !available);
      }
    });

    if (!variant) return;

    // Price block (price.tsx formatting: no decimals on whole amounts)
    const priceEl = this.querySelector("[data-price]");
    if (priceEl) priceEl.innerHTML = this.formatMoney(variant.price);

    const compareWrap = this.querySelector("[data-compare-wrap]");
    const hasCompare =
      variant.compare_at_price && variant.compare_at_price > variant.price;
    if (compareWrap) {
      compareWrap.classList.toggle("hidden", !hasCompare);
      if (hasCompare) {
        const compareEl = compareWrap.querySelector("[data-compare-price]");
        if (compareEl)
          compareEl.innerHTML = this.formatMoney(variant.compare_at_price);
        const pctEl = compareWrap.querySelector("[data-discount-pct]");
        if (pctEl) {
          pctEl.textContent = Math.round(
            ((variant.compare_at_price - variant.price) /
              variant.compare_at_price) *
              100,
          );
        }
      }
    }

    // Mega deal numbers (mega-deal.tsx)
    const priceUnits = variant.price / 100;
    const savings = Math.round(priceUnits * (this.couponValue / 100));
    const finalPrice = Math.round(priceUnits) - savings;
    this.querySelectorAll("[data-savings]").forEach((el) => {
      el.textContent = savings.toLocaleString("en-IN");
    });
    this.querySelectorAll("[data-final-price]").forEach((el) => {
      el.textContent = finalPrice.toLocaleString("en-IN");
    });

    // Stock urgency
    this.renderStockUrgency(variant);

    // Add-to-cart buttons follow the selected variant
    this.querySelectorAll("[data-add-to-cart]").forEach((button) => {
      button.dataset.variantId = variant.id;
      button.disabled = !variant.available;
      button.classList.toggle("opacity-60", !variant.available);
      button.classList.toggle("cursor-not-allowed", !variant.available);
      const priceEl = button.querySelector("[data-atc-price]");
      if (priceEl) priceEl.innerHTML = this.formatMoney(variant.price);
    });
    this.querySelectorAll("[data-buy-now]").forEach((button) => {
      button.dataset.variantId = variant.id;
    });
  }

  formatMoney(cents) {
    const units = cents / 100;
    const whole = units % 1 === 0;
    const formatted = units.toLocaleString("en-IN", {
      minimumFractionDigits: whole ? 0 : 2,
      maximumFractionDigits: whole ? 0 : 2,
    });
    return `<span class="font-medium">${this.currencySymbol}</span>${formatted}`;
  }

  /* ---------------- social proof (page.tsx title hashes) ---------------- */

  // page.tsx: ((hash << 5) - hash + charCode) | 0 over the title, then
  // (abs % 10) + 12 viewers and (abs(<<7 variant) % 6) + 20 buyers.
  paintSocialProof() {
    const title = this.product.title || "";
    let h7 = 0;
    for (let i = 0; i < title.length; i++) {
      h7 = ((h7 << 7) - h7 + title.charCodeAt(i)) | 0;
    }
    const bought = this.querySelector("[data-bought-count]");
    if (bought) bought.textContent = (Math.abs(h7) % 6) + 20;
  }

  /* ---------------- stock urgency (stock-urgency.tsx) ---------------- */

  renderStockUrgency(variant) {
    const row = this.querySelector("[data-stock-urgency]");
    if (!row) return;
    if (!variant.available) {
      row.classList.add("hidden");
      return;
    }
    row.classList.remove("hidden");

    const handle = this.dataset.productHandle || "product";
    let h1 = 0;
    let h2 = 0;
    let h3 = 0;
    for (let i = 0; i < handle.length; i++) {
      const c = handle.charCodeAt(i);
      h1 = ((h1 << 5) - h1 + c) | 0;
      h2 = ((h2 << 7) - h2 + c) | 0;
      h3 = ((h3 << 3) - h3 + c) | 0;
    }
    const pool = [
      (Math.abs(h1) % 5) + 2,
      (Math.abs(h2) % 4) + 4,
      (Math.abs(h3) % 4) + 5,
    ];

    const selectedSize = this.state.size || this.state.Size || null;
    let count = pool[0];
    if (selectedSize) {
      let h = 0;
      for (let i = 0; i < selectedSize.length; i++) {
        h = ((h << 5) - h + selectedSize.charCodeAt(i)) | 0;
      }
      count = pool[Math.abs(h) % pool.length];
    }

    const countEl = row.querySelector("[data-stock-count]");
    if (countEl) countEl.textContent = count;
    const sizeEl = row.querySelector("[data-stock-size]");
    if (sizeEl)
      sizeEl.textContent = selectedSize ? `${selectedSize} size` : "this size";
  }

  /* ---------------- countdown (countdown-timer.tsx) ---------------- */

  bindCountdown() {
    const el = this.querySelector("[data-countdown]");
    if (!el) return;
    const row = this.querySelector("[data-countdown-row]");
    const windowMs = Number(this.dataset.dealHours || 2.5) * 3600 * 1000;

    const tick = () => {
      const now = Date.now();
      const endTime = Math.floor(now / windowMs) * windowMs + windowMs;
      const seconds = Math.max(0, Math.floor((endTime - now) / 1000));
      if (seconds === 0) {
        if (row) row.classList.add("hidden");
        return;
      }
      if (row) row.classList.remove("hidden");
      const h = String(Math.floor(seconds / 3600)).padStart(2, "0");
      const m = String(Math.floor((seconds % 3600) / 60)).padStart(2, "0");
      const s = String(seconds % 60).padStart(2, "0");
      el.textContent = `${h}:${m}:${s}`;
    };
    tick();
    setInterval(tick, 1000);
  }

  /* ---------------- gallery ---------------- */

  bindGallery() {
    // Mobile dots
    const scroller = this.querySelector("[data-gallery-scroller]");
    if (scroller) {
      scroller.addEventListener("scroll", () => {
        const index = Math.round(scroller.scrollLeft / scroller.clientWidth);
        this.querySelectorAll("[data-gallery-dot]").forEach((dot) => {
          const active = Number(dot.dataset.galleryDot) === index;
          dot.classList.toggle("w-4", active);
          dot.classList.toggle("bg-dorahi-pink", active);
          dot.classList.toggle("w-1.5", !active);
          dot.classList.toggle("bg-gray-300", !active);
        });
      });
    }

    // Lightbox
    const lightbox = this.querySelector("[data-lightbox]");
    if (lightbox) {
      const items = [...this.querySelectorAll("[data-gallery-item]")];
      this.lightboxSources = [];
      const seen = new Set();
      items.forEach((item) => {
        const img = item.querySelector("img");
        const index = Number(item.dataset.galleryItem);
        if (img && !seen.has(index)) {
          seen.add(index);
          this.lightboxSources[index] = img.currentSrc || img.src;
        }
        item.addEventListener("click", () => this.openLightbox(index));
      });

      lightbox
        .querySelector("[data-lightbox-close]")
        ?.addEventListener("click", () => this.closeLightbox());
      lightbox
        .querySelector("[data-lightbox-backdrop]")
        ?.addEventListener("click", () => this.closeLightbox());
      lightbox
        .querySelector("[data-lightbox-prev]")
        ?.addEventListener("click", (e) => {
          e.stopPropagation();
          this.stepLightbox(-1);
        });
      lightbox
        .querySelector("[data-lightbox-next]")
        ?.addEventListener("click", (e) => {
          e.stopPropagation();
          this.stepLightbox(1);
        });
      document.addEventListener("keydown", (e) => {
        if (lightbox.classList.contains("hidden")) return;
        if (e.key === "Escape") this.closeLightbox();
        if (e.key === "ArrowLeft") this.stepLightbox(-1);
        if (e.key === "ArrowRight") this.stepLightbox(1);
      });
    }

    // PiP video (product-gallery.tsx)
    const pip = this.querySelector("[data-pip]");
    if (pip) {
      const video = pip.querySelector("[data-pip-video]");
      // iOS Low Power Mode bypass: retry playback on first interaction.
      const attemptPlay = () => {
        if (!video) return;
        video.muted = true;
        video.play().catch(() => {
          const retry = () => video.play().catch(() => {});
          window.addEventListener("touchstart", retry, { once: true });
          window.addEventListener("click", retry, { once: true });
        });
      };
      attemptPlay();

      pip.addEventListener("click", () => this.openVideoModal());
      pip.querySelector("[data-pip-close]")?.addEventListener("click", (e) => {
        e.stopPropagation();
        pip.remove();
      });

      const modal = this.querySelector("[data-video-modal]");
      modal
        ?.querySelector("[data-video-modal-close]")
        ?.addEventListener("click", () => this.closeVideoModal());
      modal
        ?.querySelector("[data-video-modal-backdrop]")
        ?.addEventListener("click", () => this.closeVideoModal());
    }
  }

  openLightbox(index) {
    this.lightboxIndex = index;
    const lightbox = this.querySelector("[data-lightbox]");
    lightbox.classList.remove("hidden");
    document.body.style.overflow = "hidden";
    this.paintLightbox();
  }

  closeLightbox() {
    this.querySelector("[data-lightbox]").classList.add("hidden");
    document.body.style.overflow = "";
  }

  stepLightbox(delta) {
    const total = this.lightboxSources.length;
    this.lightboxIndex = (this.lightboxIndex + delta + total) % total;
    this.paintLightbox();
  }

  paintLightbox() {
    const lightbox = this.querySelector("[data-lightbox]");
    const img = lightbox.querySelector("[data-lightbox-image]");
    if (img) img.src = this.lightboxSources[this.lightboxIndex] || "";
    const counter = lightbox.querySelector("[data-lightbox-counter]");
    if (counter)
      counter.textContent = `${this.lightboxIndex + 1} / ${this.lightboxSources.length}`;
  }

  openVideoModal() {
    const modal = this.querySelector("[data-video-modal]");
    if (!modal) return;
    modal.classList.remove("hidden");
    const player = modal.querySelector("[data-video-modal-player]");
    player?.play().catch(() => {});
  }

  closeVideoModal() {
    const modal = this.querySelector("[data-video-modal]");
    if (!modal) return;
    modal.classList.add("hidden");
    modal.querySelector("[data-video-modal-player]")?.pause();
  }

  /* ---------------- bottom sheets ---------------- */

  bindModals() {
    this.bindSheet("mega-deal");
    this.bindSheet("size-chart");

    this.querySelectorAll("[data-scroll-to-reviews]").forEach((el) => {
      el.addEventListener("click", () => {
        document
          .getElementById("reviews-section")
          ?.scrollIntoView({ behavior: "smooth" });
      });
    });
  }

  // Headless UI Transition port: toggle hidden, then flip the enter/leave
  // classes on the next frame so the CSS transition runs.
  bindSheet(name) {
    const modal = this.querySelector(`[data-${name}-modal]`);
    if (!modal) return;
    const backdrop = modal.querySelector(`[data-${name}-backdrop]`);
    const panel = modal.querySelector(`[data-${name}-panel]`);

    const open = () => {
      modal.classList.remove("hidden");
      document.body.style.overflow = "hidden";
      requestAnimationFrame(() => {
        backdrop?.classList.remove("opacity-0");
        backdrop?.classList.add("opacity-100");
        panel?.classList.remove(
          "translate-y-full",
          "md:scale-95",
          "md:opacity-0",
        );
        panel?.classList.add("translate-y-0", "md:scale-100", "md:opacity-100");
      });
    };

    const close = () => {
      backdrop?.classList.add("opacity-0");
      backdrop?.classList.remove("opacity-100");
      panel?.classList.add("translate-y-full", "md:scale-95", "md:opacity-0");
      panel?.classList.remove(
        "translate-y-0",
        "md:scale-100",
        "md:opacity-100",
      );
      document.body.style.overflow = "";
      setTimeout(() => modal.classList.add("hidden"), 200);
    };

    this.querySelectorAll(`[data-${name}-open]`).forEach((el) =>
      el.addEventListener("click", open),
    );
    modal
      .querySelectorAll(`[data-${name}-close]`)
      .forEach((el) => el.addEventListener("click", close));
    backdrop?.addEventListener("click", close);
  }

  /* ---------------- cart ---------------- */

  bindCart() {
    this.querySelectorAll("[data-add-to-cart]").forEach((button) => {
      button.addEventListener("click", () => this.addToCart(button));
    });
    this.querySelectorAll("[data-buy-now]").forEach((button) => {
      button.addEventListener("click", () => this.buyNow(button));
    });
  }

  setAtcPending(pending) {
    this.querySelectorAll("[data-add-to-cart]").forEach((button) => {
      const idle = button.querySelector("[data-atc-idle]");
      const loading = button.querySelector("[data-atc-loading]");
      idle?.classList.toggle("hidden", pending);
      loading?.classList.toggle("hidden", !pending);
      loading?.classList.toggle("flex", pending);
      button.classList.toggle("opacity-70", pending);
      button.classList.toggle("cursor-wait", pending);
    });
  }

  async addToCart(button) {
    const variantId = button.dataset.variantId;
    if (!variantId || this.atcBusy) return;
    this.atcBusy = true;
    this.setAtcPending(true);

    const routes = window.Theme?.routes ?? {};
    const formData = new FormData();
    formData.append("id", variantId);
    formData.append("quantity", "1");

    const deferred = CartLinesUpdateEvent.createPromise();
    button.dispatchEvent(
      new CartLinesUpdateEvent({
        action: "add",
        context: "product",
        lines: [{ merchandiseId: variantId, quantity: 1 }],
        promise: deferred.promise,
      }),
    );

    try {
      const response = await fetch(routes.cart_add_url || "/cart/add", {
        method: "POST",
        headers: {
          Accept: "application/json",
          "X-Requested-With": "XMLHttpRequest",
        },
        body: formData,
      });
      const result = await response.json();

      const ajaxCart = await fetch(`${routes.cart_url || "/cart"}.json`).then(
        (r) => r.json(),
      );
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
        detail: {
          items: ajaxCart.items,
          source: "dorahi-product",
          itemCount: 1,
          didError: Boolean(result.status),
        },
      });

      if (result.status) {
        console.warn(
          "[dorahi-product] add to cart failed:",
          result.message || result.description,
        );
        return;
      }

      // add-to-cart.tsx opened the cart drawer on a clean success.
      document.dispatchEvent(new CustomEvent("dorahi:cart:added"));
    } catch (error) {
      deferred.reject(error);
      console.warn("[dorahi-product] add to cart failed:", error);
    } finally {
      this.atcBusy = false;
      this.setAtcPending(false);
    }
  }

  buyNow(button) {
    const variantId = button.dataset.variantId || this.selectedVariant()?.id;
    if (!variantId) return;

    const idle = button.querySelector("[data-buy-now-idle]");
    const loading = button.querySelector("[data-buy-now-loading]");
    idle?.classList.add("hidden");
    loading?.classList.remove("hidden");
    loading?.classList.add("flex");

    try {
      if (window.shiprocketCheckoutEvents?.buyDirect) {
        window.shiprocketCheckoutEvents.buyDirect({
          type: "product",
          products: [{ variantId: String(variantId), quantity: 1 }],
        });
      } else {
        // Shiprocket embed missing — go straight to Shopify checkout instead of
        // failing silently like the app did.
        window.location.href = `/cart/${variantId}:1`;
        return;
      }
    } finally {
      setTimeout(() => {
        idle?.classList.remove("hidden");
        loading?.classList.add("hidden");
        loading?.classList.remove("flex");
      }, 1500);
    }
  }

}

if (!customElements.get("dorahi-product")) {
  customElements.define("dorahi-product", DorahiProduct);
}
